import { makeObservable, observable, action, runInAction } from "mobx";
import notificationStore from "../Notification/notificationStore";
import { BaseStore } from "../BaseStore";
import AuthStore from "../Auth/authStore";
import { AddressApi, AddressDto, PatientCoordinatesDto, SublocalityDto, ImportGeojsonResultDto } from "../../api-client";

export default class AddressStore extends BaseStore<AddressApi> {
  public apiClient: AddressApi;
  public authStore: AuthStore;
  
  public constructor(apiClient: AddressApi, authStore: AuthStore) {
    super(apiClient);
    this.apiClient = apiClient;
    this.authStore = authStore;
  }

  public getSublocalityForAddresses = async (
    locationId: string,
    addresses: AddressDto[]
  ): Promise<SublocalityDto[]> => {
    try {
      const { data } = (await this.apiClient.getSublocalityForAddresses(locationId,addresses)) as any;
      return data as SublocalityDto[];
    } catch (error) {
      console.error("Error fetching sublocalities:", error);
      notificationStore.showMessage("Failed to fetch sublocalities.", "error");
      return [];
    }
  }

  public getAllSublocalities = async (locationId: string): Promise<SublocalityDto[]> => {
    try {
      const { data } = (await this.apiClient.getAllSublocalities(locationId)) as any;
      return data as SublocalityDto[];
    } catch (error) {
      console.error("Error fetching all sublocalities:", error);
      notificationStore.showMessage("Failed to fetch all sublocalities.", "error");
      return [];
    }
  }
  public createSublocality = async (
    sublocalityDto: SublocalityDto
  ): Promise<SublocalityDto | null> => {
    try {
      const { data } = (await this.apiClient.createSublocality(sublocalityDto)) as any;
      return data as SublocalityDto;
    } catch (error) {
      console.error("Error creating sublocality:", error);
      return null;
    }
  }
  
  public updateSublocality = async (
    id: string,
    updateData: Partial<SublocalityDto>
  ): Promise<void> => {
    try {
      await this.apiClient.updateSublocality(id, updateData as SublocalityDto);
    } catch (error) {
      console.error("Error updating sublocality:", error);
      throw error;
    }
  }

  public deleteSublocality = async (id: string): Promise<void> => {
    try {
      await this.apiClient.deleteSublocality(id);
    } catch (error) {
      console.error("Error deleting sublocality:", error);
      throw error;
    }
  }

  public getPatientsCoordinates = async (locationId: string): Promise<PatientCoordinatesDto[]> => {
    try {
      const { data } = (await this.apiClient.getPatientsCoordinates(locationId)) as any;
      return data as { addressLongitude: number; addressLatitude: number; sublocalityId?: string; sublocalityColor?: string }[];
    } catch (error) {
      console.error("Error fetching patients' coordinates:", error);
      return [];
    }
  }

  public getGeojsonSublocalities = async (locationId: string): Promise<object | null> => {
    try {
      const { data } = await this.apiClient.getGeojsonSublocalities(locationId);
      return data;
    } catch (error) {
      console.error("Error exporting GeoJSON:", error);
      return null;
    }
  }

  public importGeojsonSublocalities = async (locationId: string, geojson: object): Promise<ImportGeojsonResultDto | null>  => {
    try {
      const { data } = await this.apiClient.importGeojsonSublocalities(locationId, geojson);
      return data;
    } catch (error) {
      console.error("Error importing GeoJSON:", error);
      return null;
    }
  }
}
